// AssetConnectivityDrawer — the "Edit connectivity" facet drawer (ADR-038
// drawer kind; ADR-063 §access facet as re-ruled 2026-08-30), opened from the
// Connectivity tab's seat (the page) and the peek's Edit menu — one act. Everything a
// machine needs to connect to this unit over any protocol: the address · the
// device it is reached through · the populated SIMs · the services it uses,
// with their ports, usernames, passwords, communities — and the vpn flavor.
//
// The four-layer law shapes the form: the PRODUCT's capabilities are the menu
// (one block per protocol the model can speak — a unit opens the ones it uses,
// never adds a protocol of its own); a service outside the datasheet that a
// unit already carries stays visible and removable; a unit whose product
// declares nothing is pointed at the right layer (name the product / declare
// on the model). SIM rows are "Add SIM" PROGRESSIVE slot records (sequential
// auto-assignment — the slot is required in data, never asked). Secrets are
// write-only: a set value shows a starred placeholder and "leave blank to
// keep". Blocks render from the ONE field registry in
// `@ems-app/schemas/assets/access` — never per-protocol JSX. "Accessed via" is
// the family's field (ADR-080 — `Picker` over `assetSource`) on the unit LOOKUP
// inside this site (ADR-078 §6 — `reached_through`: in service and
// network-capable, the units whose PRODUCT fronts others first, a unit on hold
// greyed once typed, never the unit itself; the rule is the server's); the
// field stands INSIDE the site (`must_belong` — here the site never changes;
// the wizard clears the via at its page level when its site does, the step
// being off screen); on a fresh declaration the field pre-fills when the site
// holds exactly ONE such unit — the start answer's one likely row — and says
// so. A unit reached through another device types the ports dialed ON that
// device, and its own address reads "Local address (on the gateway's LAN)".
//
// One body, two containers: `useConnectivityForm` + `ConnectivityFields` are
// the shared half the add wizard's Connectivity step mounts inside the
// Stepper; this file's drawer is the edit container. `onSubmit(facts, stamp)` is
// the contract — the page maps it onto the edit verb; the Workbench story mocks
// it. The edit rides the row stamp (ADR-084): the form seeds from the facet read
// as it opened, the save carries that read's stamp with the facts whole, and a
// stale save shows the line with Load the latest — the host's `reload`, the
// facet read again.

import { useEffect, useId, useMemo } from 'react';
import { Controller, useFieldArray, type Path, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import {
  DEVICE_ACCESS_FIELDS,
  DEVICE_ACCESS_KINDS,
  DeviceAccessEntrySchema,
  DeviceAccessKindSchema,
  IpAddressSchema,
  serviceKindsOfCapabilities,
  type DeviceAccessEntry,
  type DeviceAccessFieldDef,
  type DeviceAccessKind,
  type SimSlot,
} from '@ems-app/schemas/assets/access';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import type { AssetAccess, DeviceAccessViewEntry } from '@ems-app/schemas/assets/responses';
import type { AssetLookupRow } from '@ems-app/schemas/assets/lookup';

import { useFormShell, type UseFormShellReturn } from '../../components/patterns/forms/useFormShell.js';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { defaultPortOf, simSlotsOf } from '../../components/patterns/connectivityDisplay.js';
import { Button } from '../../components/ui/Button.js';
import { Picker } from '../../components/patterns/Picker.js';
import { useAssetFace } from '../../components/patterns/lookupFaces.js';
import { assetSource } from '../../hooks/useAssets.js';
import { useLookup, type LookupSource } from '../../hooks/useLookup.js';
import { codeSource } from '../../hooks/useOptions.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { NumberInput } from '../../components/ui/NumberInput.js';
import { icons } from '../../components/ui/icons.js';
import { formatNumber } from '../../lib/format.js';

// ── the contract ─────────────────────────────────────────────────────────────

/** The "reached through" question: the unit lookup inside the unit's site,
 *  under the `reached_through` rule, never the unit itself. `null` when the
 *  unit sits nowhere a via can be picked (a depot, a vehicle). */
export type ViaSource = LookupSource<'assets'>;
export function viaSourceOf(siteId: string | null | undefined, selfId?: string): ViaSource | null {
  return siteId ? { entity: 'assets', params: { rule: 'reached_through', site: siteId, self: selfId } } : null;
}

export interface ConnectivityFacts {
  deviceIp: string | null;
  accessedViaAssetId: string | null;
  deviceAccess: DeviceAccessEntry[];
}

// ── forms: ONE builder per kind, from the registry ──────────────────────────

type ServiceValues = Record<string, string | number | null>;

const E164 = /^\+[1-9]\d{6,14}$/;
const IMSI = /^\d{6,15}$/;
// Blank = unset; anything typed must be a real address (the shared IP law —
// `device_ip` is inet-grade, the APN fields must not store junk silently).
const blankOrIp = z.union([z.literal(''), IpAddressSchema]);

function fieldSchema(f: DeviceAccessFieldDef): z.ZodType {
  switch (f.kind) {
    case 'port':
      return z.number().int().min(1).max(65535).nullable();
    case 'int':
      return z
        .number()
        .int()
        .min(f.min ?? 0)
        .max(f.max ?? Number.MAX_SAFE_INTEGER)
        .nullable();
    case 'enum':
      return f.required ? z.string().min(1) : z.string().nullable();
    case 'secret':
      return z.string().max(255); // '' = keep the sealed value
    case 'phone':
      return z.union([z.literal(''), z.string().trim().regex(E164, 'PHONE_E164')]);
    case 'url':
      return z.string().trim().max(255);
    case 'ip':
      return blankOrIp;
    default:
      return f.required ? z.string().trim().min(1).max(64) : z.string().trim().max(64);
  }
}

function serviceSchema(kind: DeviceAccessKind) {
  const shape: Record<string, z.ZodType> = {};
  for (const f of DEVICE_ACCESS_FIELDS[kind]) shape[f.key] = fieldSchema(f);
  return z.object(shape);
}

/** The edit form's seed for one kind — secrets are never seeded (write-only). */
function serviceValues(kind: DeviceAccessKind, entry: DeviceAccessViewEntry | null): ServiceValues {
  const out: ServiceValues = {};
  for (const f of DEVICE_ACCESS_FIELDS[kind]) {
    const v = entry?.fields[f.key];
    if (f.kind === 'port' || f.kind === 'int') out[f.key] = typeof v === 'number' ? v : null;
    else if (f.kind === 'enum') out[f.key] = typeof v === 'string' ? v : null;
    else if (f.kind === 'secret') out[f.key] = '';
    else out[f.key] = typeof v === 'string' ? v : '';
  }
  return out;
}

const anyFilled = (values: ServiceValues): boolean =>
  Object.values(values).some((v) => (typeof v === 'string' ? v.trim() !== '' : v !== null && v !== undefined));

/** Form → the wire entry: blanks are omitted (a blank secret = keep). */
function toEntry(kind: DeviceAccessKind, values: ServiceValues, hasVia: boolean): DeviceAccessEntry {
  const raw: Record<string, unknown> = { kind };
  for (const f of DEVICE_ACCESS_FIELDS[kind]) {
    // a viaOnly field (local_port) has no meaning on a Direct unit — never carry
    // a stale forwarded value onto a now-Direct entry (it is hidden at render,
    // so it would persist invisibly across a via-clear).
    if (f.viaOnly && !hasVia) continue;
    const v = values[f.key];
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') {
      const s = v.trim();
      if (s !== '') raw[f.key] = s;
    } else {
      raw[f.key] = v;
    }
  }
  return DeviceAccessEntrySchema.parse(raw);
}

const ServiceBlockSchema = z.object({
  kind: DeviceAccessKindSchema,
  /** The product speaks it — the block is part of the menu (collapsible, never removable). */
  fromProduct: z.boolean(),
  /** The unit already carries an entry for it (a blank save keeps the row + its sealed secrets). */
  declared: z.boolean(),
  /** Expanded — the unit uses (or is setting up) this service. */
  open: z.boolean(),
  values: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
});

// One "Add SIM" row — a slot record's asked fields (the slot number itself is
// sequential, never asked; a flip chip renders the row's number).
const SimRowSchema = z.object({
  imsi: z.union([z.literal(''), z.string().trim().regex(IMSI, 'IMSI_DIGITS')]),
  msisdn: z.union([z.literal(''), z.string().trim().regex(E164, 'PHONE_E164')]),
  apnIp: blankOrIp,
  apnIpBackup: blankOrIp,
});

export const ConnectivityFormSchema = z
  .object({
    deviceIp: blankOrIp,
    accessedViaAssetId: z.string().nullable(),
    sims: z.array(SimRowSchema).max(4),
    services: z.array(ServiceBlockSchema),
  })
  .superRefine((v, ctx) => {
    // Each OPEN block validates against its kind's shape; issues land on the field.
    v.services.forEach((s, i) => {
      if (!s.open) return;
      const r = serviceSchema(s.kind).safeParse(s.values);
      if (!r.success) {
        for (const issue of r.error.issues) {
          ctx.addIssue({ code: 'custom', message: issue.message, path: ['services', i, 'values', ...issue.path] });
        }
      }
    });
  });
export type ConnectivityFormValues = z.infer<typeof ConnectivityFormSchema>;

const simRowOf = (s: SimSlot | undefined): z.infer<typeof SimRowSchema> => ({
  imsi: s?.imsi ?? '',
  msisdn: s?.msisdn ?? '',
  apnIp: s?.apn_ip ?? '',
  apnIpBackup: s?.apn_ip_backup ?? '',
});

/** The form's seed from the facet read (the drawer AND the wizard step share
 *  it — one seeding law): declared slot records seed their rows in slot order;
 *  a single-slot product opens with its one row ready. */
export function connectivityInitial(access: AssetAccess, defaultVia: string | null): ConnectivityFormValues {
  const declaredSims = simSlotsOf(access);
  const product = access.model ? serviceKindsOfCapabilities(access.model.capabilities) : [];
  return {
    deviceIp: access.device_ip ?? '',
    accessedViaAssetId: access.accessed_via?.id ?? defaultVia,
    sims:
      declaredSims.length > 0
        ? Array.from({ length: Math.max(...declaredSims.map((s) => s.slot)) }, (_, i) =>
            simRowOf(declaredSims.find((s) => s.slot === i + 1)),
          )
        : (access.model?.sims ?? 0) === 1
          ? [simRowOf(undefined)]
          : [],
    services: DEVICE_ACCESS_KINDS.flatMap((kind) => {
      if (kind === 'cellular') return [];
      const entry = access.entries.find((e) => e.kind === kind) ?? null;
      const fromProduct = product.includes(kind);
      if (!entry && !fromProduct) return [];
      return [{ kind, fromProduct, declared: entry !== null, open: entry !== null, values: serviceValues(kind, entry) }];
    }),
  };
}

/** Form values → the edit verb's connectivity facts (shared by both containers). */
export function connectivityFacts(v: ConnectivityFormValues): ConnectivityFacts {
  const hasVia = Boolean(v.accessedViaAssetId);
  const deviceAccess: DeviceAccessEntry[] = v.services
    .filter((b) => b.open && (b.declared || anyFilled(b.values)))
    .map((b) => toEntry(b.kind, b.values, hasVia));
  // The SIM rows ARE the cellular entry — slot = the row's position
  // (sequential auto-assignment); an all-blank row simply drops, so a
  // cleared record removes the entry honestly.
  const sims: SimSlot[] = v.sims.flatMap((s, i) => {
    const rec: SimSlot = { slot: i + 1 };
    if (s.imsi.trim()) rec.imsi = s.imsi.trim();
    if (s.msisdn.trim()) rec.msisdn = s.msisdn.trim();
    if (s.apnIp.trim()) rec.apn_ip = s.apnIp.trim();
    if (s.apnIpBackup.trim()) rec.apn_ip_backup = s.apnIpBackup.trim();
    return Object.keys(rec).length > 1 ? [rec] : [];
  });
  if (sims.length > 0) deviceAccess.push(DeviceAccessEntrySchema.parse({ kind: 'cellular', sims }));
  return {
    deviceIp: v.deviceIp.trim() === '' ? null : v.deviceIp.trim(),
    accessedViaAssetId: v.accessedViaAssetId,
    deviceAccess,
  };
}

/** Whether the person gave the form anything — an address, a SIM, an opened service with a value, a gateway of their
 *  own (the site's pre-filled one is not an answer). Read raw: the values need not validate yet. */
export function connectivityAnswered(v: ConnectivityFormValues, defaultVia: string | null): boolean {
  return (
    v.deviceIp.trim() !== '' ||
    v.sims.some((s) => [s.imsi, s.msisdn, s.apnIp, s.apnIpBackup].some((x) => x.trim() !== '')) ||
    v.services.some((b) => b.open && anyFilled(b.values)) ||
    (!!v.accessedViaAssetId && v.accessedViaAssetId !== defaultVia)
  );
}

// ── the shared body: the form + the fields (drawer now, the wizard step too) ─

export interface UseConnectivityFormOptions {
  /** The facet read the form opens on — its stamp rides the save. */
  access: AssetAccess;
  assetCategory: AssetCategory;
  /** The start answer's `fronts` rows — the site's units whose product fronts others (the lookup's suggestion group). */
  likely: readonly Pick<AssetLookupRow, 'id'>[];
  /** Load the latest: the facet read again (quiet), or null when the unit is gone — the host's read, as the save is. */
  reload?: () => Promise<AssetAccess | null>;
  /** The save: the facts WHOLE, with the facet read's stamp as the form opened (undefined on a story's mock). */
  onSubmit: (facts: ConnectivityFacts, stamp: number | undefined) => Promise<void>;
  onSuccess?: () => void;
}

export interface ConnectivityForm {
  shell: UseFormShellReturn<ConnectivityFormValues>;
  /** The default that was applied on a fresh declaration (the site's one unit built to carry others), if any. */
  defaultVia: string | null;
}

/** The fresh-declaration default: the site's ONE unit whose product fronts
 *  others — read from the lookup's start answer, where the likely rows lead
 *  (so "exactly one" is certain from the first page). An existing choice is
 *  never overridden; a gateway itself gets none. */
export function defaultViaOf(access: AssetAccess, assetCategory: AssetCategory, likely: readonly Pick<AssetLookupRow, 'id'>[]): string | null {
  const fresh = !access.device_ip && !access.accessed_via && access.entries.length === 0;
  return access.accessed_via ? null : fresh && assetCategory !== 'gateway' && likely.length === 1 ? likely[0]!.id : null;
}

/** The parent rule for a via field that may be OFF SCREEN when its site changes (the wizard's Connectivity step is
 *  unmounted while the site is changed on Location — ADR-080 §9 applied at the page): a SITE
 *  change clears the via whole, dirty or not, NOT said (the person arrives at a fresh step; the typed services stay);
 *  the site's one unit built to carry others pre-fills an EMPTY via when it resolves (empty → value never clears; a
 *  value the person set stays — the seeded-form rule). */
export function useViaUnderSite(form: UseFormReturn<ConnectivityFormValues>, siteId: string | null, defaultVia: string | null): void {
  useEffect(() => {
    if (form.getValues('accessedViaAssetId') !== null) form.setValue('accessedViaAssetId', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on the site's change alone
  }, [siteId]);
  useEffect(() => {
    if (defaultVia !== null && !form.getValues('accessedViaAssetId')) form.setValue('accessedViaAssetId', defaultVia);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- when the default resolves
  }, [defaultVia]);
}

export function useConnectivityForm({ access, assetCategory, likely, reload, onSubmit, onSuccess }: UseConnectivityFormOptions): ConnectivityForm {
  const defaultVia = defaultViaOf(access, assetCategory, likely);

  // The seed is the shell's: read from the facet as it opens (and as Load the latest brings it), never reactively.
  const shell = useFormShell<ConnectivityFormValues, AssetAccess>({
    schema: ConnectivityFormSchema,
    row: access,
    values: (r) => {
      const a = r ?? access;
      return connectivityInitial(a, defaultViaOf(a, assetCategory, likely));
    },
    reload,
    onSubmit: async (v, { stamp }) => onSubmit(connectivityFacts(v), stamp),
    onSuccess,
  });

  return { shell, defaultVia };
}

export function ConnectivityFields({
  form,
  access,
  projectId,
  viaSource,
  defaultVia,
}: {
  form: UseFormReturn<ConnectivityFormValues>;
  access: AssetAccess;
  projectId: string;
  /** The "reached through" question — null where no via can be picked (a depot, a vehicle). */
  viaSource: ViaSource | null;
  defaultVia: string | null;
}) {
  const { t } = useTranslation('assets');
  const ipId = useId();
  const { fields, remove } = useFieldArray({ control: form.control, name: 'services' });
  const simRows = useFieldArray({ control: form.control, name: 'sims' });
  const viaId = form.watch('accessedViaAssetId');
  // The first service IN USE that has a port — where the drawer says, once, which port is which.
  const inUse = form.watch('services');
  const firstPorted = inUse.findIndex((b) => b.open && DEVICE_ACCESS_FIELDS[b.kind].some((f) => f.key === 'port'));
  const face = useAssetFace();
  // The via's name for the "port on X" labels — from the lookup's known rows
  // (the same read the field rides; a chosen via outside the site is named by
  // its id — with its reference, as a unit is always named), else the loaded
  // target. A via that sits OUTSIDE the picker's rule (on hold / cross-site)
  // must still render the Via face, never the Direct one. (An out-of-scope
  // target is redacted server-side → stays null.)
  const viaLook = useLookup(projectId, viaSource ?? { entity: 'assets', params: {} }, useMemo(() => (viaId ? [viaId] : []), [viaId]), viaSource !== null);
  const viaRow = viaId ? viaLook.known.get(viaId) : undefined;
  // The field's source — the same question as the start answer's (one cache entry), as the family's descriptor.
  const viaSite = viaSource?.params.site ?? null;
  const viaSelf = viaSource?.params.self;
  const viaPicker = useMemo(() => (viaSite ? assetSource(projectId, 'reached_through', face, { site: viaSite, self: viaSelf }) : null), [projectId, face, viaSite, viaSelf]);
  const viaName = viaId ? (viaRow ? (face(viaRow).selectedLabel ?? face(viaRow).label) : access.accessed_via?.id === viaId ? access.accessed_via.label : null) : null;

  const nudge =
    access.model === null
      ? t('device.nudge.generic')
      : access.model.capabilities.length === 0
        ? access.model.is_local
          ? t('device.nudge.localNoCapabilities')
          : t('device.nudge.canonicalNoCapabilities')
        : null;

  const secretsSetOf = (kind: DeviceAccessKind) => access.entries.find((e) => e.kind === kind)?.secrets_set ?? [];
  // "Add SIM" stays offered up to the product's slot count; a generic or LOCAL
  // record has no reliable datasheet, so allow one row beyond the declared set
  // (honest data first — a physically-fitted SIM can always be declared). A
  // canonical model's slot count is authoritative: a 0-slot canonical shows none.
  const unbounded = access.model === null || access.model.is_local;
  const maxSims = Math.min(4, unbounded ? simRows.fields.length + 1 : Math.max(access.model?.sims ?? 0, simRows.fields.length));
  const canAddSim = simRows.fields.length < maxSims;

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{t('device.sections.reach')}</h3>
        <Controller
          control={form.control}
          name="accessedViaAssetId"
          render={({ field, fieldState }) =>
            viaPicker ? (
              <Picker
                source={viaPicker}
                dependsOn={{ value: viaSite, case: 'must_belong' }}
                label={t('device.fields.accessedVia')}
                value={field.value}
                onChange={field.onChange}
                placeholder={t('device.viaPicker.none')}
                clearable
                error={fieldState.error?.message}
                help={defaultVia !== null && field.value === defaultVia ? t('device.viaPicker.default') : t('device.path.viaHint')}
              />
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">{t('device.viaPicker.noSite')}</p>
            )
          }
        />
        <div>
          <Label htmlFor={ipId}>{viaName ? t('device.fields.localIp') : t('device.fields.deviceIp')}</Label>
          <Input id={ipId} dir="ltr" autoComplete="off" {...form.register('deviceIp')} error={form.formState.errors.deviceIp?.message} />
        </div>
      </section>

      {maxSims > 0 && (
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{t('device.sections.sims')}</h3>
          <p className="text-xs text-[var(--color-text-secondary)]">{t('device.sim.hint', { count: access.model?.sims ?? simRows.fields.length })}</p>
          {simRows.fields.map((row, i) => (
            <SimRow key={row.id} index={i} form={form} t={t} />
          ))}
          {canAddSim && (
            <div>
              <Button variant="link" onClick={() => simRows.append(simRowOf(undefined))}>
                <icons.add size={14} aria-hidden />
                {t('device.sim.add')}
              </Button>
            </div>
          )}
        </section>
      )}

      <section className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{t('device.sections.services')}</h3>
        {nudge && <p className="text-sm text-[var(--color-text-secondary)]">{nudge}</p>}
        {fields.map((block, i) => (
          <ServiceBlock
            key={block.id}
            index={i}
            kind={block.kind}
            fromProduct={block.fromProduct}
            viaName={viaName}
            portHelp={i === firstPorted}
            secretsSet={secretsSetOf(block.kind)}
            form={form}
            onRemove={block.fromProduct ? undefined : () => remove(i)}
            t={t}
          />
        ))}
      </section>
    </div>
  );
}

// ── the drawer container ─────────────────────────────────────────────────────

export interface AssetConnectivityDrawerProps {
  access: AssetAccess;
  assetCategory: AssetCategory;
  projectId: string;
  viaSource: ViaSource | null;
  /** The start answer's likely rows (the pre-fill's input) — the host waits for the start answer before mounting. */
  likely: readonly Pick<AssetLookupRow, 'id'>[];
  open: boolean;
  onClose: () => void;
  /** Load the latest — the facet read again (the host's read, as the save is). */
  reload?: () => Promise<AssetAccess | null>;
  onSubmit: (facts: ConnectivityFacts, stamp: number | undefined) => Promise<void>;
}

export function AssetConnectivityDrawer({ access, assetCategory, projectId, viaSource, likely, open, onClose, reload, onSubmit }: AssetConnectivityDrawerProps) {
  const { t } = useTranslation('assets');
  const { shell, defaultVia } = useConnectivityForm({ access, assetCategory, likely, reload, onSubmit, onSuccess: onClose });
  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={t('device.drawer.title')}
      description={t('device.drawer.hint')}
      submitLabel={t('edit.submit')}
      maxWidth="max-w-xl"
    >
      <ConnectivityFields form={shell.form} access={access} projectId={projectId} viaSource={viaSource} defaultVia={defaultVia} />
    </DrawerForm>
  );
}

// ── one SIM slot row — identity anchor + number + the APN pair ──────────────

function SimRow({ index, form, t }: { index: number; form: UseFormReturn<ConnectivityFormValues>; t: TFunction<'assets'> }) {
  const baseId = useId();
  const errors = form.formState.errors.sims?.[index];
  return (
    <fieldset className="rounded-lg border border-[var(--color-border)] p-3.5">
      <legend className="text-sm font-semibold text-[var(--color-text-primary)]">{t('device.sim.label', { n: formatNumber(index + 1) })}</legend>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${baseId}-imsi`}>{t('device.sim.imsi')}</Label>
          <Input
            id={`${baseId}-imsi`}
            dir="ltr"
            inputMode="numeric"
            autoComplete="off"
            {...form.register(`sims.${index}.imsi` as const)}
            error={errors?.imsi?.message}
          />
        </div>
        <div>
          <Label htmlFor={`${baseId}-number`}>{t('device.sim.number')}</Label>
          <Input
            id={`${baseId}-number`}
            dir="ltr"
            inputMode="tel"
            autoComplete="off"
            placeholder="+9665…"
            {...form.register(`sims.${index}.msisdn` as const)}
            error={errors?.msisdn?.message}
          />
        </div>
        <div>
          <Label htmlFor={`${baseId}-apn`}>{t('device.sim.apn')}</Label>
          <Input id={`${baseId}-apn`} dir="ltr" autoComplete="off" {...form.register(`sims.${index}.apnIp` as const)} error={errors?.apnIp?.message} />
        </div>
        <div>
          <Label htmlFor={`${baseId}-apnb`}>{t('device.sim.apnBackup')}</Label>
          <Input
            id={`${baseId}-apnb`}
            dir="ltr"
            autoComplete="off"
            {...form.register(`sims.${index}.apnIpBackup` as const)}
            error={errors?.apnIpBackup?.message}
          />
        </div>
      </div>
    </fieldset>
  );
}

// ── one service block — the registry's fields, edit (collapsed = not used) ──

function ServiceBlock({
  index,
  kind,
  fromProduct,
  viaName,
  portHelp,
  secretsSet,
  form,
  onRemove,
  t,
}: {
  index: number;
  kind: DeviceAccessKind;
  fromProduct: boolean;
  viaName: string | null;
  /** The first service in use that has a port says which port is which — once in the drawer, never a line per service. */
  portHelp: boolean;
  secretsSet: string[];
  form: UseFormReturn<ConnectivityFormValues>;
  /** Only a service outside the product's datasheet can be removed. */
  onRemove?: () => void;
  t: TFunction<'assets'>;
}) {
  const baseId = useId();
  // The protocol's lists (auth · priv · scheme · flavor) as the family's code sources — built once per kind and language.
  const enumSources = useMemo(
    () =>
      new Map(
        DEVICE_ACCESS_FIELDS[kind]
          .filter((f) => f.kind === 'enum')
          .map((f) => [
            f.key,
            codeSource(
              `device.${kind}.${f.key}`,
              (f.options ?? []).map((o) => ({ id: o, label: kind === 'vpn' ? t(`device.vpnFlavor.${o}`) : f.key === 'scheme' ? o : o.toUpperCase(), pickable: true, group: null })),
            ),
          ]),
      ),
    [kind, t],
  );
  const name = (key: string) => `services.${index}.values.${key}` as Path<ConnectivityFormValues>;
  const openName = `services.${index}.open` as const;
  const open = form.watch(openName);
  const declared = form.getValues(`services.${index}.declared` as const);
  const errorOf = (key: string): string | undefined => {
    const e = form.formState.errors.services?.[index]?.values as Record<string, { message?: string }> | undefined;
    return e?.[key]?.message;
  };
  const kindLabel = t(`device.kinds.${kind}`);

  // Collapsed: the product can speak it, this unit doesn't use it (yet).
  if (!open) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-[var(--color-border)] px-3.5 py-2.5">
        <div className="min-w-0">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">{kindLabel}</span>
          <span className="ms-2 text-xs text-[var(--color-text-secondary)]">{t('device.notUsed')}</span>
        </div>
        <Button variant="link" onClick={() => form.setValue(openName, true, { shouldDirty: true })}>
          <icons.add size={14} aria-hidden />
          {t('device.setUp')}
        </Button>
      </div>
    );
  }

  // The web port's blank placeholder derives from the picked SCHEME (80/443) —
  // the one dynamic default in the registry.
  const scheme = kind === 'web' ? form.watch(name('scheme')) : null;
  const portPlaceholder = (f: DeviceAccessFieldDef): string | undefined => {
    if (viaName && f.key === 'port') return undefined; // a forwarded port has no default
    const def =
      kind === 'web'
        ? defaultPortOf('web', { fields: { scheme: typeof scheme === 'string' ? scheme : 'http' } })
        : (f.defaultValue ?? (f.key === 'local_port' ? defaultPortOf(kind) : null));
    return def === null || def === undefined ? undefined : String(def);
  };

  const field = (f: DeviceAccessFieldDef) => {
    const id = `${baseId}-${f.key}`;
    const label = f.key === 'port' && viaName ? t('device.fields.portOn', { name: viaName }) : t(`device.fields.${f.key}`);
    if (f.kind === 'port' || f.kind === 'int') {
      return (
        <Controller
          key={f.key}
          control={form.control}
          name={name(f.key)}
          render={({ field: rhf, fieldState }) => (
            <div>
              <Label htmlFor={id}>{label}</Label>
              <NumberInput
                id={id}
                value={typeof rhf.value === 'number' ? rhf.value : null}
                onChange={rhf.onChange}
                placeholder={portPlaceholder(f)}
                error={fieldState.error?.message}
              />
              {/* Behind a gateway the two ports are easy to swap: say which is which, once, under the first dialed one. */}
              {viaName && portHelp && f.key === 'port' && <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('device.fields.portOnHelp', { name: viaName })}</p>}
            </div>
          )}
        />
      );
    }
    if (f.kind === 'enum') {
      const source = enumSources.get(f.key)!;
      return (
        <Controller
          key={f.key}
          control={form.control}
          name={name(f.key)}
          render={({ field: rhf, fieldState }) => (
            <Picker source={source} label={label} value={typeof rhf.value === 'string' ? rhf.value : null} onChange={rhf.onChange} clearable={!f.required} error={fieldState.error?.message} />
          )}
        />
      );
    }
    if (f.kind === 'secret') {
      const set = secretsSet.includes(f.key);
      return (
        <div key={f.key}>
          <Label htmlFor={id}>{label}</Label>
          <Input id={id} type="password" autoComplete="off" placeholder={set ? '••••••••' : undefined} {...form.register(name(f.key))} error={errorOf(f.key)} />
          {set && <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('device.secret.keepHint')}</p>}
        </div>
      );
    }
    return (
      <div key={f.key}>
        <Label htmlFor={id} required={f.required}>
          {label}
        </Label>
        <Input id={id} dir={f.kind === 'text' ? undefined : 'ltr'} autoComplete="off" {...form.register(name(f.key))} error={errorOf(f.key)} />
      </div>
    );
  };

  // `viaOnly` fields (local_port) are asked on Via units only (the registry's
  // own flag — the forward-remap truth has no meaning on a Direct unit).
  const blockFields = DEVICE_ACCESS_FIELDS[kind].filter((f) => !f.viaOnly || viaName);

  return (
    <fieldset className="rounded-lg border border-[var(--color-border)] p-3.5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <legend className="text-sm font-semibold text-[var(--color-text-primary)]">{kindLabel}</legend>
          <p className="text-xs text-[var(--color-text-secondary)]">{fromProduct ? t('device.fromProduct') : t('device.notInProduct')}</p>
        </div>
        {onRemove ? (
          <IconButton aria-label={t('device.drawer.removeService', { kind: kindLabel })} onClick={onRemove}>
            <icons.close size={16} aria-hidden />
          </IconButton>
        ) : (
          !declared && (
            // Setting up, not saved yet — fold the block back (nothing is written).
            <Button variant="link" tone="neutral" onClick={() => form.setValue(openName, false, { shouldDirty: true })}>
              {t('device.notUsedAction')}
            </Button>
          )
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{blockFields.map(field)}</div>
    </fieldset>
  );
}
